import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedItem } from '@/components/itemList/types';
import type { RootState } from './index';
import { updateCurrentUserGroup } from '../app/features/workspace/workspaceSlice';

// ── Constants ────────────────────────────────────────────────────────

/** Cache entries older than this trigger a background refresh. */
export const CACHE_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

// ── Types ────────────────────────────────────────────────────────────

/** Minimal metadata about the parent container (for breadcrumbs on cache-read). */
export interface ParentMeta {
  id: string;
  title?: string;
  itemtype?: string;
  breadcrumb?: unknown;
  [key: string]: unknown;
}

interface ParentCache {
  items: NormalizedItem[];
  parentMeta?: ParentMeta;
  fetchedAt: number;
}

interface ItemsState {
  byParent: Record<string, ParentCache>;
}

// ── Slice ────────────────────────────────────────────────────────────

const initialState: ItemsState = {
  byParent: {},
};

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    /** Store fetched items (and optional parent metadata) for a parent container. */
    setItemsForParent(
      state,
      action: PayloadAction<{ parentId: string; items: NormalizedItem[]; parentMeta?: ParentMeta }>,
    ) {
      state.byParent[action.payload.parentId] = {
        items: action.payload.items,
        parentMeta: action.payload.parentMeta,
        fetchedAt: Date.now(),
      };
    },

    /** Optimistic update: patch fields on a single item. */
    updateCachedItem(
      state,
      action: PayloadAction<{ parentId: string; itemId: string; changes: Partial<NormalizedItem> }>,
    ) {
      const cache = state.byParent[action.payload.parentId];
      if (!cache) return;
      const idx = cache.items.findIndex((i) => i.id === action.payload.itemId);
      if (idx >= 0) {
        cache.items[idx] = { ...cache.items[idx], ...action.payload.changes };
      }
    },

    /** Optimistic delete: remove items from a parent. */
    removeCachedItems(
      state,
      action: PayloadAction<{ parentId: string; itemIds: string[] }>,
    ) {
      const cache = state.byParent[action.payload.parentId];
      if (!cache) return;
      const idSet = new Set(action.payload.itemIds);
      cache.items = cache.items.filter((i) => !idSet.has(i.id));
    },

    /** Clear a specific parent's cache. */
    invalidateParent(state, action: PayloadAction<string>) {
      delete state.byParent[action.payload];
    },

    /** Clear all cached items (e.g. logout). */
    clearAllItems(state) {
      state.byParent = {};
    },
  },
  extraReducers: (builder) => {
    // Clear items cache when workspace switches — items are workspace-scoped
    builder.addCase(updateCurrentUserGroup, (state) => {
      state.byParent = {};
    });
  },
});

export const {
  setItemsForParent,
  updateCachedItem,
  removeCachedItems,
  invalidateParent,
  clearAllItems,
} = itemsSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────

export const selectItemsForParent =
  (parentId: string | undefined) =>
  (state: RootState): NormalizedItem[] | undefined => {
    if (!parentId) return undefined;
    return state.items.byParent[parentId]?.items;
  };

export const selectParentMeta =
  (parentId: string | undefined) =>
  (state: RootState): ParentMeta | undefined => {
    if (!parentId) return undefined;
    return state.items.byParent[parentId]?.parentMeta;
  };

export const selectCacheAge =
  (parentId: string | undefined) =>
  (state: RootState): number | undefined => {
    if (!parentId) return undefined;
    const cache = state.items.byParent[parentId];
    if (!cache) return undefined;
    return Date.now() - cache.fetchedAt;
  };

export default itemsSlice.reducer;
